import type { SpacePlanningTranslations } from "../en/SpacePlanning";
import type { TranslationShape } from "../../store/types";

export const spacePlanningEs: TranslationShape<SpacePlanningTranslations> = {
  pageTitle: "Planificación de Espacios",
  pageSubtitle:
    "Revisa, edita y aprueba planogramas pendientes, visualiza planogramas activos o consulta registros históricos",

  banner: {
    title: "Da Vida a Tus Planogramas",
    description:
      "Gestiona el ciclo de vida completo del planograma en un solo lugar. Revisa los planogramas pendientes generados a partir de tus simulaciones de surtido previas, valida sus métricas de rendimiento y edítalos según sea necesario antes de aprobarlos para activación — o monitorea los planogramas activos en todas tus tiendas. Usa filtros y acciones masivas para optimizar tu flujo de trabajo y avanzar más rápido de la decisión al despliegue.",
  },

  tabs: {
    pending: "Planogramas Pendientes",
    active: "Planogramas Activos",
    historical: "Planogramas Históricos",
  },

  filters: {
    viewOn: "Ver en:",
    allChains: "Todas las Cadenas",
    allStores: "Todas las Tiendas",
    allCategories: "Todas las Categorías",
    searchPlaceholder: "Buscar por tienda, ID o SIM-id...",
    filterPogs: "Filtrar POGs por:",
    latest: "Más reciente",
    oldest: "Más antiguo",
    activeFilters: "Filtros activos:",
    clearAll: "Limpiar todo",
    commercialChain: "Cadena Comercial",
    store: "Tienda",
    category: "Categoría",
    assortment: "Surtido",
    search: "Búsqueda",
    searchButton: "Buscar",
  },

  pagination: {
    rowsPerPage: "Filas por página:",
  },

  results: {
    showing: "Mostrando {{start}}-{{end}} de {{total}} tiendas",
    selected: "({{count}} seleccionadas)",
  },

  columns: {
    storeCode: "Código de Tienda",
    pogName: "Nombre POG",
    storeStatus: "Estado de Tienda",
    pogId: "ID POG",
    category: "Categoría",
    assortment: "Surtido",
    returnOnSpace: "Retorno de Espacio",
    coreRange: "Rango Principal",
    shelfAlignment: "Alineación de Estante",
    dogms: "DOGMS",
    size: "Tamaño",
    warnings: "Alertas",
    lastEdited: "Última Edición",
    archivedAt: "Archivado",
    actions: "Acciones",
    view: "Ver",
    analysis: "Análisis",
    logs: "Registros",
  },

  status: {
    pending: "Pendiente",
    active: "Activo",
    historical: "Histórico",
    notAvailable: "No disponible",
    loading: "Cargando planogramas...",
    loadError: "No se pudieron cargar los planogramas.",
    loadErrorToastTitle: "Error al cargar datos de planificación de espacios",
    loadErrorToastMessage: "No se pudieron cargar estos planogramas: {{sources}}.",
    processing: "Procesando...",
    error: "Error al procesar",
    updateFailed: "Actualización fallida",
    updateFailedDetail:
      "La actualización se ejecutó pero ninguna de las inserciones, eliminaciones o intercambios aprobados se aplicó. El planograma refleja el diseño original, no los cambios aprobados.",
    updatePartial: "Aplicada parcialmente",
    updatePartialDetail:
      "La actualización se ejecutó y algunos de los cambios aprobados (inserciones, eliminaciones, intercambios) se aplicaron. Otros fueron omitidos (sin anaquel compatible, impacto en PNL u otras restricciones de Frogger).",
  },

  actions: {
    update: "Actualizar",
    approve: "Aprobar",
    edit: "Editar",
    viewEdit: "Ver / Editar",
    updateSelected: "Actualizar Seleccionados ({{count}})",
    approveSelected: "Aprobar Seleccionados ({{count}})",
    deleteSelected: "Eliminar Seleccionados ({{count}})",
    violations: "Violaciones ({{count}})",
    reports: "Reportes ({{count}})",
    storeIntegrityReport: "Reporte de Integridad de Tienda",
    categoryRangeReviewReport: "Reporte de Revisión de Rango por Categoría",
    export: "Exportar ({{count}})",
  },

  toasts: {
    approvalsSuccess: "Planogramas aprobados",
    approvalsError: "No se pudieron aprobar los planogramas",
    missingPogReference: "Falta la referencia de merchflow/POG para esta fila",
    pogDataUnavailable: "Los datos del POG no están disponibles",
  },

  noResults: "No se encontraron planogramas",

  logs: {
    title: "Registro de Actividad",
    exportLogs: "Exportar Registros",
    entries: {
      activated: "Planograma Activado",
      analysisRun: "Análisis de Rendimiento Ejecutado",
      compliancePassed: "Verificación de Cumplimiento Aprobada",
      warningDetected: "Alerta Detectada",
      auditCompleted: "Auditoría de Tienda Completada",
    },
  },

  preview: {
    title: "Vista Previa del Planograma",
    category: "Categoría",
    assortment: "Surtido",
    activeDate: "Fecha de Activación",
    size: "Tamaño",
    segments: "{{count}} segmentos",
    performanceMetrics: "Métricas de Rendimiento",
    status: "Estado",
    returnOnSpace: "Retorno de Espacio",
    coreRange: "Rango Principal",
    shelfAlignment: "Alineación de Estante",
    dogms: "DOGMS",
    planogramLayout: "Diseño del Planograma",
    shelf: "Estante {{number}}",
    section: "Sección {{brand}}",
    totalSegments: "Total de Segmentos",
    totalSkus: "Total de SKUs",
    close: "Cerrar",
    export: "Exportar",
    editPlanogram: "Editar Planograma",
  },

  history: {
    title: "Historial del Planograma",
    approvedBy: "Aprobado por:",
    activated: "Activado:",
    deactivated: "Desactivado:",
    close: "Cerrar",
    exportHistory: "Exportar Historial",
  },

  confirmUpdate: {
    singleTitle: "Confirmar Actualización del Planograma",
    singleSubtitle: "Revisa los cambios antes de continuar",
    bulkTitle: "Confirmar Actualización Masiva",
    bulkSubtitle: "Estás a punto de actualizar {{count}} planogramas",
    bulkSummary: "Resumen de Actualización Masiva",
    bulkSummaryDesc: "{{count}} planogramas serán activados",
    whatWillHappen: "Qué sucederá:",
    step1Title: "Activar nuevo planograma",
    step1Desc: "La versión {{id}} será establecida como Activa",
    step2Title: "Actualizar planograma de tienda",
    step2Desc: "El planograma será marcado como aprobado y activo en Planogramas",
    step3Title: "Archivar versión anterior",
    step3Desc: "El planograma activo actual será archivado (si existe)",
    step4Title: "Remover de pendientes",
    step4Desc: "Este planograma será removido de la cola de Planificación de Espacios",
    planogramDetails: "Detalles del Planograma",
    categoryLabel: "Categoría:",
    sizeLabel: "Tamaño:",
    sizeSegments: "{{count}} segmentos",
    activeDateLabel: "Fecha de Activación:",
    endDateLabel: "Fecha de Fin:",
    selectedPlanograms: "Planogramas Seleccionados ({{count}}):",
    warningTitle: "Esta acción no se puede deshacer",
    warningDesc:
      "Una vez activado, este planograma reemplazará inmediatamente al actual y afectará las operaciones de la tienda.",
    warningDescBulk:
      "Una vez activados, estos {{count}} planogramas reemplazarán inmediatamente a los actuales y afectarán las operaciones de las tiendas.",
    cancel: "Cancelar",
    confirm: "Confirmar Actualización",
    confirmBulk: "Confirmar Actualización ({{count}})",
    na: "N/A",
  },

  confirmDelete: {
    title: "Eliminar Planogramas Seleccionados",
    subtitle: "Estás a punto de eliminar {{count}} planograma(s) pendiente(s)",
    selectedPlanograms: "Planogramas Seleccionados ({{count}}):",
    warningTitle: "Esta acción no se puede deshacer",
    warningDesc:
      "Estos {{count}} planograma(s) se eliminarán de la cola de Planeación de Espacio. Los planogramas que se estén procesando se omitirán.",
    cancel: "Cancelar",
    confirm: "Eliminar Seleccionados ({{count}})",
    toasts: {
      deletedWithSkipped:
        "Se eliminaron {{deleted}} planograma(s), se omitieron {{skipped}} en proceso",
      deleted: "Planogramas eliminados",
      failed: "Error al eliminar los planogramas",
      couldNotDelete:
        "No se pudieron eliminar {{count}} planograma(s) — falta la referencia del planograma",
    },
  },
};
